import { X, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import { useState } from 'react';

interface TransferInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBank?: string;
}

export default function TransferInstructionsModal({ 
  isOpen, 
  onClose, 
  selectedBank 
}: TransferInstructionsModalProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const bankInfo = {
    'Banrural': {
      name: 'Banrural',
      accountNumber: '3173027242',
      accountHolder: 'Distribuidora del sol Guatemala S.A.',
      accountType: 'Cuenta Monetaria'
    },
    'BI': {
      name: 'Banco Industrial',
      accountNumber: '1100021030',
      accountHolder: 'Distribuidora del sol Guatemala S.A.',
      accountType: 'Cuenta Monetaria'
    },
    'BAM': {
      name: 'BAM',
      accountNumber: '3040076937',
      accountHolder: 'Distribuidora del sol Guatemala S.A.',
      accountType: 'Cuenta Monetaria'
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Instrucciones para Pago por Transferencia
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Paso 1 */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                1
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">
                  Selecciona tu banco
                </h3>
                <p className="text-blue-800 text-sm">
                  Elige el banco desde el cual realizarás la transferencia. Puedes usar cualquiera de nuestras cuentas disponibles.
                </p>
              </div>
            </div>
          </div>

          {/* Información bancaria */}
          {selectedBank && bankInfo[selectedBank as keyof typeof bankInfo] && (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">
                Información de la cuenta seleccionada
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Banco:</span>
                  <span className="font-medium">{bankInfo[selectedBank as keyof typeof bankInfo].name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tipo de cuenta:</span>
                  <span className="font-medium">{bankInfo[selectedBank as keyof typeof bankInfo].accountType}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Número de cuenta:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium">
                      {bankInfo[selectedBank as keyof typeof bankInfo].accountNumber}
                    </span>
                    <button
                      onClick={() => copyToClipboard(
                        bankInfo[selectedBank as keyof typeof bankInfo].accountNumber, 
                        'account'
                      )}
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {copiedField === 'account' ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">A nombre de:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-right max-w-xs">
                      {bankInfo[selectedBank as keyof typeof bankInfo].accountHolder}
                    </span>
                    <button
                      onClick={() => copyToClipboard(
                        bankInfo[selectedBank as keyof typeof bankInfo].accountHolder, 
                        'holder'
                      )}
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {copiedField === 'holder' ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Paso 2 */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                2
              </div>
              <div>
                <h3 className="font-semibold text-green-900 mb-2">
                  Realiza la transferencia
                </h3>
                <div className="text-green-800 text-sm space-y-2">
                  <p>• Accede a tu banca en línea o aplicación móvil</p>
                  <p>• Busca la opción "Transferencias" o "Pagos"</p>
                  <p>• Ingresa los datos de la cuenta seleccionada</p>
                  <p>• Transfiere el monto exacto de tu pedido</p>
                  <p>• Guarda el comprobante de la transferencia</p>
                </div>
              </div>
            </div>
          </div>

          {/* Paso 3 */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                3
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 mb-2">
                  Sube el comprobante
                </h3>
                <p className="text-purple-800 text-sm">
                  Una vez realizada la transferencia, toma una foto o captura de pantalla del comprobante y súbelo en el campo correspondiente de esta página.
                </p>
              </div>
            </div>
          </div>

          {/* Paso 4 */}
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                4
              </div>
              <div>
                <h3 className="font-semibold text-orange-900 mb-2">
                  Confirma tu pedido
                </h3>
                <p className="text-orange-800 text-sm">
                  Haz clic en "Pagar" para enviar tu pedido. Nuestro equipo verificará el pago y te notificará cuando sea confirmado.
                </p>
              </div>
            </div>
          </div>

          {/* Notas importantes */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="flex-shrink-0 w-6 h-6 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">
                  Notas importantes
                </h3>
                <div className="text-yellow-800 text-sm space-y-1">
                  <p>• El monto debe ser exacto al total de tu pedido</p>
                  <p>• La transferencia puede tardar hasta 24 horas en procesarse</p>
                  <p>• Tu pedido quedará pendiente hasta confirmar el pago</p>
                  <p>• Solo aceptamos transferencias desde cuentas bancarias guatemaltecas</p>
                  <p>• El comprobante debe ser legible y mostrar todos los datos de la transacción</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tiempo de procesamiento */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">
              ⏱️ Tiempo de procesamiento
            </h3>
            <div className="text-gray-700 text-sm space-y-1">
              <p>• <strong>Transferencia inmediata:</strong> 2-4 horas</p>
              <p>• <strong>Transferencia normal:</strong> 24-48 horas</p>
              <p>• <strong>Verificación del pago:</strong> 1-2 horas después de recibir el comprobante</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
} 